import { Component, OnInit, OnDestroy } from '@angular/core';
import { EncounterService } from '../../../../core/services/encounter.service';
import { EncounterActionType } from '../../../../shared/models/enctr/encounter-action-type';
import { EncounterActionTypeCategory } from '../../../../shared/models/enctr/encounter-action-type-category';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EncounterType } from '../../../../shared/models/enctr/encounter-type';
import { AppMenuService } from '@nx-ahs/shared/services';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { PageHeaderComponent, AppSpinnerComponent, ProgressButtonComponent } from '@nx-ahs/shared/components';

@Component({
	selector: 'app-encounter-action-note-management',
	templateUrl: './encounter-action-note-management.component.html',
	styleUrls: ['./encounter-action-note-management.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		PageHeaderComponent,
		AppSpinnerComponent,
		ProgressButtonComponent
	]
})
export class EncounterActionNoteManagementComponent implements OnInit, OnDestroy {
	public encounterActionTypes: EncounterActionType[];
	public encounterActionCategories: EncounterActionTypeCategory[] = [];
	public filteredEncounterActionTypes: EncounterActionType[];
	public filteredEncounterActionCategories: EncounterActionTypeCategory[];
	public encounterTypes: EncounterType[];

	public templateForm: FormGroup = new FormGroup({
		encounterType: new FormControl(undefined, Validators.required),
		encounterCategoryType: new FormControl(undefined),
		encounterActionType: new FormControl(undefined, Validators.required),
		noteTemplate: new FormControl('', Validators.pattern(/^(?:[^{}]?({[^{}]+})?)*$/))
	});

	public saving: boolean;
	public loading: boolean;
	public subscriptions: Subscription[] = [];
	public useCategoryFilter = false;
	public typeLabel: string = 'Interaction Type';
	public actionLabel: string = 'Interaction Action';
	public categoryLabel: string = 'Interaction Category';

	constructor(
		private encounterService: EncounterService,
		private appMenuService: AppMenuService,
		private snackBar: MatSnackBar
	) {}

	ngOnInit(): void {
		const menu = this.appMenuService.appMenuConfig
			.find((x) => x.title === 'Tools')
			?.childMenu.find((x) => x.url === '/action-note-management');
		if (menu?.metadata) {
			this.useCategoryFilter = menu.metadata.useCategoryFilter === true;
			this.typeLabel = menu.metadata.typeLabel || this.typeLabel;
			this.actionLabel = menu.metadata.actionLabel || this.actionLabel;
			this.categoryLabel = menu.metadata.categoryLabel || this.categoryLabel;
		}

		this.encounterService
			.getActionTypes()
			.toPromise()
			.then((result) => {
				// set encounteractiontypes and order
				this.encounterActionTypes = result.sort((a, b) => {
					if (a.description < b.description) {
						return -1;
					}
					if (a.description > b.description) {
						return 1;
					}
					return 0;
				});

				result.forEach((r) => {
					const match = this.encounterActionCategories.find(
						(p) => p.encounterActionTypeCategoryId == r.encounterActionTypeCategoryId
					);

					if (!match) {
						this.encounterActionCategories.push(r.encounterActionTypeCategory);
					}
				});

				this.encounterActionCategories.sort((a, b) => a.description.localeCompare(b.description));
			})

			// get encounter types
			.then(() => {
				return this.encounterService.getEncounterTypes(false).toPromise();
			})

			// show only encounter types that have a encounteractiontype
			.then((results) => {
				const encounterTypes: EncounterType[] = [];

				results.forEach((r) => {
					const map = this.encounterActionTypes.find((p) => p.encounterTypeId == r.encounterTypeId);

					if (map) {
						encounterTypes.push(r);
					}
				});

				this.encounterTypes = encounterTypes;
			});

		this.subscriptions.push(
			this.templateForm.get('encounterType').valueChanges.subscribe((value) => {
				this.filteredEncounterActionCategories = this.encounterActionCategories.filter((x) =>
					this.encounterActionTypes.some(
						(y) =>
							y.encounterActionTypeCategoryId == x.encounterActionTypeCategoryId &&
							y.encounterTypeId == value?.encounterTypeId
					)
				);
				this.refreshActionTypes();
			})
		);

		this.subscriptions.push(
			this.templateForm.get('encounterActionType').valueChanges.subscribe((x) => {
				this.templateForm.get('noteTemplate').setValue((x as EncounterActionType)?.noteTemplate);
			})
		);

		if (this.useCategoryFilter) {
			this.subscriptions.push(
				this.templateForm.get('encounterCategoryType').valueChanges.subscribe(() => this.refreshActionTypes())
			);
		}
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((x) => x.unsubscribe());
	}

	save() {
		this.saving = true;
		this.loading = true;

		const actionType: EncounterActionType = this.templateForm.get('encounterActionType').value;

		actionType.noteTemplate = this.templateForm.get('noteTemplate').value;

		this.encounterService
			.saveEncounterActionType(actionType)
			.toPromise()
			.finally(() => {
				this.snackBar.open('Interaction action note template saved.', undefined, { duration: 5000 });
				this.saving = false;
				this.loading = false;
				this.templateForm.reset();
				this.filteredEncounterActionTypes = [];
			});
	}

	cancel() {}

	private refreshActionTypes() {
		const selectedType = this.templateForm.get('encounterType').value as EncounterType;
		const selectedAction = this.templateForm.get('encounterActionType').value as EncounterActionType;
		const selectedCategory = this.templateForm.get('encounterCategoryType').value as EncounterActionType;

		this.filteredEncounterActionTypes = this.encounterActionTypes
			.filter(
				(x) =>
					x.encounterTypeId == selectedType?.encounterTypeId &&
					x.encounterActionTypeCategoryId ==
						(selectedCategory?.encounterActionTypeCategoryId ?? x.encounterActionTypeCategoryId)
			)
			.sort((a, b) => a.description.localeCompare(b.description));

		if (selectedAction) {
			const actionType = this.filteredEncounterActionTypes.find(
				(x) => x.description == selectedAction.description
			);
			if (actionType && this.useCategoryFilter) this.templateForm.get('encounterActionType').setValue(actionType);
			else this.templateForm.get('encounterActionType').setValue(undefined);
		}
	}
}
